import type { Registry as Services } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { tKey } from 'ember-intl';
import type { StayRestrictionsAccordionState } from 'lumen/components/hotel/pricing-assistant/table/modals/day-override-modal/stay-restrictions-accordion';
import type {
  Period,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type {
  MinLosOverride,
  OverbookingOverride,
} from 'lumen/models/override';
import type { RestrictionOverridePayload } from 'lumen/services/repositories/restriction-action';
import {
  MIN_LOS_DEFAULT_VALUE,
  type MinLosRestrictionType,
  OVERRIDE_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type {
  RateGroupContextFields,
  RestrictionActionValue,
} from 'lumen/utils/constants/restriction-actions';
import {
  hasRestrictionActionChanged,
  minLosRestrictionValues,
  minLosTypeToValue,
  minLosValueToType,
} from 'lumen/utils/pricing-assistant/restriction-action-utils';
import {
  formatMinLosRecommendation,
  formatMinLosValue,
  minLosErrorMessage,
} from 'lumen/utils/pricing-assistant/restriction-display-utils';

export type SendRestrictionConfig = RateGroupContextFields & {
  existingOverride: MinLosOverride | OverbookingOverride | null | undefined;
  getRestrictionValues: () => RestrictionActionValue[] | null;
  hasError: () => boolean;
  markTouched: () => void;
  overrideRemoved: boolean;
  overrideType: RestrictionOverridePayload['type'];
  overrideValue: number | null;
  restriction: Restriction | undefined;
  restrictionRecommendation: RestrictionRecommendation | undefined;
};

interface MinLosRestrictionStateOptions {
  /* Discriminates hotel level (no rateGroupId) from a rate group (required
     rateGroupId) and is echoed into the send config. */
  contextFields: RateGroupContextFields;
  /* A thunk rather than the period itself so reads follow the modal's @period arg if
     the caller replaces it; the modal's integration tests simulate a re-request that
     way. */
  getPeriod: () => Period;
  /* Optional because only rate-group contexts warn when the hotel MinLOS takes
     precedence; the message needs the hotel state to compare against, so it stays a
     concern of the owner holding both instances. Receives the instance being
     warned about. */
  getWarningMessage?: (state: MinLosRestrictionState) => string | null;
  intl: Services['intl'];
  /* Modal-wide send-phase signals; see the modal's isPreparingSend /
     isPushingRestrictionAction getters for what each phase covers. */
  isPreparingSend: () => boolean;
  isPushingRestrictionAction: () => boolean;
  shouldShowStayRestrictionException: () => boolean;
}

/* Form state for one MinLOS restriction context (hotel level or a rate group) in the
   day-override-modal. Owns the user's unsaved edits and derives everything else live
   from Period, so the modal holds a collection of instances instead of a bespoke
   set of variables per restriction type.

   Instances must be created once per modal open (the modal's constructor):
   initiallyHadOverride snapshots there, and rebuilding an instance mid-send would
   reset it — see the overrideRemoved getter. */
export default class MinLosRestrictionState {
  @tracked automationOnUserEdit?: boolean;
  @tracked restrictionType: MinLosRestrictionType;
  @tracked touched = false;
  @tracked userEdit?: number | null;

  /* Snapshot at modal open — read live it would flip after the override delete + the
     restrictions re-request, breaking overrideRemoved mid-send. */
  readonly initiallyHadOverride: boolean;

  onBlur = (): void => {
    this.touched = true;
  };
  onTypeChange = (event: Event): void => {
    /* The value input is intentionally not updated — its value is preserved so
       switching back to MIN_LOS restores whatever the user had typed before. */
    this.restrictionType = (event.target as HTMLInputElement)
      .value as MinLosRestrictionType;
  };
  private readonly options: MinLosRestrictionStateOptions;

  reset = (): void => {
    this.userEdit = undefined;
    this.restrictionType = this.seedType;
  };
  toggleAutomation = (event: Event): void => {
    const checked = (event.target as HTMLInputElement).checked;
    this.automationOnUserEdit = checked;
    this.reset();

    if (!checked) {
      this.touched = false;
    }
  };

  updateValue = (value: number | null): void => {
    this.userEdit = value;
  };

  /* Satisfies StayRestrictionsAccordionState, so the accordion consumes an instance
     without knowing about this class. */
  get accordionState(): StayRestrictionsAccordionState {
    return {
      automationOn: this.automationOn,
      currentLabel: this.formattedCurrentLabel,
      errorMessage: this.errorMessage,
      failedValue: this.failedValue,
      formattedRecommendation: this.formattedRecommendation,
      hasOverride: this.hasOverride,
      isLoading: this.isLoading,
      onBlur: this.onBlur,
      onTypeChange: this.onTypeChange,
      restrictionType: this.restrictionType,
      showException: this.showException,
      toggleAutomation: this.toggleAutomation,
      touched: this.touched,
      updateValue: this.updateValue,
      value: this.value,
      warningMessage: this.warningMessage,
    };
  }

  get automationOn(): boolean {
    return resolveUserEdit(this.automationOnUserEdit, this.savedAutomationOn);
  }

  /* True when the sendable state differs from what is in place, i.e. the user actually
     changed it. The in-place value must be the seed value, not the raw
     minLosOverride.value: during a failed push the BE workaround reports the override
     as absent so the input seeds to MIN_LOS_DEFAULT_VALUE, while the restriction still
     carries the failed value. Baselining against the seed keeps the comparison in step
     with what the input shows, so an untouched failed override is not read as a
     pending change. */
  get changed(): boolean {
    return hasRestrictionActionChanged(
      {
        automationOn: this.automationOn,
        type: this.restrictionType,
        value: minLosTypeToValue(this.restrictionType, this.value),
      },
      {
        automationOn: this.savedAutomationOn,
        type: this.seedType,
        value: this.seedValue,
      },
    );
  }

  /* An untouched input shows no error. */
  get errorMessage(): string | null {
    return this.touched ? this.validationError : null;
  }

  private get failedValue(): number | null {
    return this.period.failedMinLosValue(this.options.contextFields);
  }

  private get formattedCurrentLabel(): string {
    /* tKey lets ember-intl-lint see the key: intl comes in via options, not
       an injected service, so the linter can't find the call on its own. */
    return this.options.intl.t(
      tKey('pricing_manager.restrictions.current_value'),
      { value: this.formattedCurrentValue },
    );
  }

  /* The MinLOS currently applied in the PMS. */
  get formattedCurrentValue(): string {
    return formatMinLosValue({
      intl: this.options.intl,
      value: this.restriction?.minLosValue,
    });
  }

  get formattedRecommendation(): string {
    return formatMinLosRecommendation({
      displayedValue: this.restriction?.minLosValue,
      intl: this.options.intl,
      recommendedValue: this.restrictionRecommendation?.minLosValue,
    });
  }

  get hasActionInProgress(): boolean {
    return this.period.hasMinLosActionInProgress(this.options.contextFields);
  }

  get hasOverride(): boolean {
    return this.period.hasMinLosOverride(this.options.contextFields);
  }

  /* The three checks map to the three phases a send goes through, each gated on this
     instance's own signals so a sibling's action never drives it:
     1. `overrideRemoved && isPreparingSend` — deleting the override (automation
        checkbox ticked) and the shared restrictions/recommendations re-request.
     2. `willSend && isPushingRestrictionAction` — the createRestrictionAction POST is
        in flight; `hasActionInProgress` is not active yet since it relies on the
        restriction-action record landing in the store.
     3. `hasActionInProgress` — the restriction-action has landed and its pollers have
        started. */
  get isLoading(): boolean {
    return (
      (this.overrideRemoved && this.options.isPreparingSend()) ||
      (this.willSend && this.options.isPushingRestrictionAction()) ||
      this.hasActionInProgress
    );
  }

  /* True when the user has toggled automation ON from an initial OFF state, meaning
     the existing override will be deleted on send. */
  get overrideRemoved(): boolean {
    return this.automationOn && this.initiallyHadOverride;
  }

  /* Read through getPeriod on every access so values stay live through the delete →
     re-request → push sequence in the modal's send flow, where restriction records
     are unloaded and re-fetched. */
  private get period(): Period {
    return this.options.getPeriod();
  }

  get restriction(): Restriction | undefined {
    return this.period.restrictionFor(this.options.contextFields);
  }

  get restrictionRecommendation(): RestrictionRecommendation | undefined {
    return this.period.restrictionRecommendationFor(this.options.contextFields);
  }

  private get savedAutomationOn(): boolean {
    return (
      this.restriction?.minLosRestrictionValueObject?.autopilotEnabled ?? false
    );
  }

  private get seedType(): MinLosRestrictionType {
    return minLosValueToType(this.seedValue);
  }

  /* Seed value for the MinLOS input and radio type. hasOverride is already false
     during a failed push (the failed override is treated as absent), so the input and
     radio fall back to the MIN_LOS default. */
  private get seedValue(): number {
    return (
      (this.hasOverride ? this.restriction?.minLosOverride?.value : null) ??
      MIN_LOS_DEFAULT_VALUE
    );
  }

  get showException(): boolean {
    return (
      this.options.shouldShowStayRestrictionException() &&
      this.failedValue != null
    );
  }

  /* Validity regardless of touched, so the warning can defer to an invalid value
     before the error shows on blur. Automation has no value to validate. */
  get validationError(): string | null {
    if (this.automationOn) {
      return null;
    }

    return minLosErrorMessage({
      intl: this.options.intl,
      restrictionType: this.restrictionType,
      value: this.value,
    });
  }

  get value(): number | null {
    return resolveUserEdit(this.userEdit, this.seedValue);
  }

  /* Reactive like other warnings in the app (no touched gate). Automation has no
     override to warn about, and an invalid value shows its error alone rather than
     stacking the warning on top. */
  get warningMessage(): string | null {
    if (this.automationOn || this.validationError) {
      return null;
    }

    return this.options.getWarningMessage?.(this) ?? null;
  }

  /* True when this instance's change will be sent on the next push, covering both a
     removal and a value change with automation off. */
  get willSend(): boolean {
    return this.changed && (!this.automationOn || this.overrideRemoved);
  }

  constructor(options: MinLosRestrictionStateOptions) {
    this.options = options;
    this.initiallyHadOverride = this.period.hasMinLosOverride(
      this.options.contextFields,
    );
    this.restrictionType = this.seedType;
  }

  toSendConfig(): SendRestrictionConfig {
    return {
      ...this.options.contextFields,
      existingOverride: this.restriction?.minLosOverride,
      getRestrictionValues: () =>
        minLosRestrictionValues(
          this.overrideRemoved,
          this.restrictionRecommendation?.minLosValue,
          this.restrictionType,
          this.value,
        ),
      hasError: () => this.errorMessage !== null,
      markTouched: (): void => {
        this.touched = true;
      },
      overrideRemoved: this.overrideRemoved,
      overrideType: OVERRIDE_TYPES.MIN_LOS,
      overrideValue: minLosTypeToValue(this.restrictionType, this.value),
      restriction: this.restriction,
      restrictionRecommendation: this.restrictionRecommendation,
    };
  }
}

/* Returns the user's edit when present, otherwise the value from the API. `undefined`
   means "not edited"; a `null`/`false` edit is a real value the user set, so we test
   against `undefined` rather than falling back with `??`. */
function resolveUserEdit<T>(userEdit: T | undefined, liveValue: T): T {
  return userEdit === undefined ? liveValue : userEdit;
}
